import { CLIENT_ACTION_VALUES } from "@platform/rfp-core";

import { isValidDate, isValidEmail } from "../utils";
import {
  CLIENT_TYPES,
  CLIENT_FORM,
  CLIENT_REDUCER_ACTIONS,
  IS_CLIENT_FORM_VALID,
  CUSTOM_CONF_FIELDS,
} from "../constants/client";

const {
  CLIENT_ID,
  BROKER_TYPE,
  CLIENT_TYPE,
  NAME,
  TOTAL_BENEFITS,
  CLIENT_SINCE,
  SIC,
  STREET_1,
  STREET_2,
  CITY,
  STATE,
  SITUS_STATE,
  ZIP_CODE,
  PRACTICE,
  CORPORATE_ENTITY,
  ONEDIGITAL_OFFICE,
  OFFICE,
  PRODUCER_NAME,
  PRODUCER_FIRST_NAME,
  PRODUCER_LAST_NAME,
  PRODUCER_EMAIL,
  CLIENT_EXECUTIVE,
} = CLIENT_FORM;
const { IS_NEW_CLIENT, IS_CURRENT_CLIENT } = CLIENT_ACTION_VALUES;
const { UPDATE_CLIENT_FIELD, UPDATE_CLIENT, CLEAN_CLIENT } =
  CLIENT_REDUCER_ACTIONS;

const initStateClientProspectInfo = {
  [CLIENT_ID]: null,
  [BROKER_TYPE]: "",
  [CLIENT_TYPE]: null,
  [NAME]: "",
  [TOTAL_BENEFITS]: "",
  [CLIENT_SINCE]: null,
  [SIC]: "",
  [STREET_1]: "",
  [STREET_2]: "",
  [CITY]: "",
  [STATE]: null,
  [ZIP_CODE]: "",
  [IS_CURRENT_CLIENT]: false,
  [IS_NEW_CLIENT]: true,
  [CUSTOM_CONF_FIELDS]: null,
  [CLIENT_EXECUTIVE]: "",
  [PRACTICE]: null,
  [CORPORATE_ENTITY]: null,
  [ONEDIGITAL_OFFICE]: null,
  [OFFICE]: null,
  [PRODUCER_NAME]: "",
  [PRODUCER_FIRST_NAME]: "",
  [PRODUCER_LAST_NAME]: "",
  [PRODUCER_EMAIL]: "",
  [SITUS_STATE]: null,
};

const initStateClientInfo = {
  ...initStateClientProspectInfo,
  [CLIENT_SINCE]: null,
};

const initState = {
  ...initStateClientInfo,
  errors: {},
  [IS_CLIENT_FORM_VALID]: false,
};

const validateForm = (data, validationType) =>
  Object.keys(data).every((key) =>
    validationType[key] ? validationType[key](data[key], data) : true,
  );

const normalizeData = (data, structure) =>
  Object.keys(data).reduce(
    (acc, key) => ({
      ...acc,
      [key]: structure[key] ? structure[key](data[key], data) : data[key],
    }),
    {},
  );

const normalizeClient = (data = []) => normalizeData(data, normalizeClientData);

const findStateMatch = (states, value) =>
  value
    ? states?.find((a) => a.abbreviation === value || a.name === value) || null
    : null;

const addressFieldDefaults = [
  [STREET_1, (av) => av.address?.[STREET_1], ""],
  [STREET_2, (av) => av.address?.[STREET_2], ""],
  [CITY, (av) => av.address?.city, ""],
  [ZIP_CODE, (av) => av.address?.zipCode, ""],
];

const additionalInfoDefaults = [
  [CLIENT_EXECUTIVE, (av) => av.additionalInfo?.clientExecutive, ""],
  [CORPORATE_ENTITY, (av) => av.additionalInfo?.corporateEntity, null],
  [ONEDIGITAL_OFFICE, (av) => av.additionalInfo?.onedigitalOffice, null],
  [OFFICE, (av) => av.additionalInfo?.officeLocation, null],
  [PRACTICE, (av) => av.additionalInfo?.practice, null],
];

const producerFieldDefaults = [
  [PRODUCER_FIRST_NAME, (av) => av?.[PRODUCER_FIRST_NAME], ""],
  [PRODUCER_LAST_NAME, (av) => av?.[PRODUCER_LAST_NAME], ""],
  [PRODUCER_EMAIL, (av) => av?.[PRODUCER_EMAIL], ""],
];

const applyDefaults = (newData, actionValue, fieldDefs) => {
  for (const [key, getter, fallback] of fieldDefs) {
    newData[key] = getter(actionValue) || fallback;
  }
};

const populateFields = (newData, actionValue) => {
  applyDefaults(newData, actionValue, addressFieldDefaults);
  newData[STATE] = findStateMatch(
    actionValue.states,
    actionValue.address?.state,
  );
  newData[SITUS_STATE] = findStateMatch(
    actionValue.states,
    actionValue?.[SITUS_STATE],
  );
  applyDefaults(newData, actionValue, additionalInfoDefaults);
  applyDefaults(newData, actionValue, producerFieldDefaults);
};

const computeErrors = (newData) =>
  Object.keys(clientInfoValidations).reduce((acc, key) => {
    const error = clientInfoValidations[key](newData[key], newData);
    return error ? { ...acc, [key]: error } : acc;
  }, {});

const handleUpdateClient = (state, action) => {
  const isNewClient = CLIENT_TYPES.NEW_CLIENT === action.value[CLIENT_TYPE];
  const isProspectClient =
    CLIENT_TYPES.PROSPECT_CLIENT === action.value[CLIENT_TYPE];
  const isCurrentClient =
    CLIENT_TYPES.CURRENT_CLIENT === action.value[CLIENT_TYPE];
  const initialStateClient = isProspectClient
    ? initStateClientProspectInfo
    : initStateClientInfo;
  const newData = normalizeClient({
    ...initialStateClient,
    ...state,
    ...action.value,
  });
  if (!isProspectClient) {
    newData[CLIENT_SINCE] = action.value.clientSince || null;
  }
  populateFields(newData, action.value);

  return {
    ...newData,
    errors: computeErrors(newData),
    [IS_NEW_CLIENT]: isNewClient,
    [IS_CURRENT_CLIENT]: isCurrentClient,
    [IS_CLIENT_FORM_VALID]: validateForm(
      newData,
      formValidationMap[isProspectClient][true],
    ),
  };
};

const handleUpdateClientField = (state, action) => {
  if (!action.name) throw Error("Field name missing");
  const newData = { ...state, [action.name]: action.value };
  const isProspectClient =
    CLIENT_TYPES.PROSPECT_CLIENT === newData[CLIENT_TYPE];
  if (isProspectClient && newData[CLIENT_SINCE]) {
    delete newData[CLIENT_SINCE];
  }
  const isClientValidFns = formValidationMap[isProspectClient][true];
  return {
    ...newData,
    [IS_CLIENT_FORM_VALID]: validateForm(newData, isClientValidFns),
    ...(clientInfoValidations[action.name]
      ? {
          errors: {
            ...newData.errors,
            [action.name]:
              clientInfoValidations[action.name](action.value, newData) || null,
          },
        }
      : null),
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case UPDATE_CLIENT:
      return handleUpdateClient(state, action);
    case CLEAN_CLIENT:
      return {
        ...initState,
        [CUSTOM_CONF_FIELDS]: state[CUSTOM_CONF_FIELDS],
        [IS_CLIENT_FORM_VALID]: false,
        [CLIENT_TYPE]: action.value,
      };
    case UPDATE_CLIENT_FIELD:
      return handleUpdateClientField(state, action);
    default:
      throw Error(`Unknown action: ${action.type}`);
  }
};

const normalizeClientData = {
  [CLIENT_ID]: (value) => parseInt(value),
  [TOTAL_BENEFITS]: (value) => (value ? parseInt(value) : null),
  [STATE]: (value, state) =>
    state.states?.find((a) => a.abbreviation === value) || value,
  [SITUS_STATE]: (value, state) =>
    state.states?.find((a) => a.abbreviation === value) || value,
};
const clientInfoValidations = {
  [ZIP_CODE]: (value) =>
    !/^\d{5}(?:-\d{4})?$/.test(value) && "Please enter a valid zip code.",
  [TOTAL_BENEFITS]: (value) =>
    (isNaN(value) || value <= 0) &&
    "Please enter a valid number of total benefits eligible employees.",
  [CLIENT_SINCE]: (value) =>
    !isValidDate(value, true) && "Invalid date format.",
  [PRODUCER_EMAIL]: (value) =>
    value && !isValidEmail(value, true) && "Invalid Email.",
};

export const isConfFieldsValid = {
  [CLIENT_EXECUTIVE]: (value, data) =>
    data[CUSTOM_CONF_FIELDS]?.[CLIENT_EXECUTIVE] ? !!value.trim() : true,
  [PRACTICE]: (value, data) =>
    data[CUSTOM_CONF_FIELDS]?.[PRACTICE] ? !!value?.value : true,
  [CORPORATE_ENTITY]: (value, data) =>
    data[CUSTOM_CONF_FIELDS]?.[CORPORATE_ENTITY] ? !!value?.value : true,
  [OFFICE]: (value, data) =>
    data[CUSTOM_CONF_FIELDS]?.[OFFICE] ? !!value?.value : true,
  [ONEDIGITAL_OFFICE]: (value, data) => {
    const isRequired =
      data[CUSTOM_CONF_FIELDS]?.[ONEDIGITAL_OFFICE] &&
      data[CUSTOM_CONF_FIELDS]?.onedigitalOfficeMode;
    return isRequired ? !!value : true;
  },
};

export const isNewClientFieldsValid = {
  [PRODUCER_EMAIL]: (value) => isValidEmail(value),
  [PRODUCER_FIRST_NAME]: (value) => !!value,
  [PRODUCER_LAST_NAME]: (value) => !!value,
  [SITUS_STATE]: (value) => !!value,
};

const isClientProspectFormValid = {
  [NAME]: (value) => !!value.trim(),
  [TOTAL_BENEFITS]: (value) => !!value && !isNaN(value) && value > 0,
  [STREET_1]: (value) => !!value,
  [CITY]: (value) => !!value.trim(),
  [STATE]: (value) => !!value,
  [ZIP_CODE]: (value) => /^\d{5}(?:-\d{4})?$/.test(value),
  [PRODUCER_NAME]: (value) => !!value,
  ...isConfFieldsValid,
};

const isNewClientProspectFormValid = {
  [NAME]: (value) => !!value.trim(),
  [TOTAL_BENEFITS]: (value) => !!value && !isNaN(value) && value > 0,
  [STREET_1]: (value) => !!value,
  [CITY]: (value) => !!value.trim(),
  [STATE]: (value) => !!value,
  [ZIP_CODE]: (value) => /^\d{5}(?:-\d{4})?$/.test(value),
  ...isNewClientFieldsValid,
  ...isConfFieldsValid,
};

const isClientFormValid = {
  ...isClientProspectFormValid,
  [CLIENT_SINCE]: (value) => !!value,
};

const isNewClientFormValid = {
  ...isNewClientProspectFormValid,
};

const formValidationMap = {
  true: {
    true: isNewClientProspectFormValid,
    false: isClientProspectFormValid,
  },
  false: { true: isNewClientFormValid, false: isClientFormValid },
};

export default reducer;
export { initState };
